// Каталог установленных продуктов (двери рельса) + живое обновление счётчиков/статусов.
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import * as platformMenu from "../api/platformMenu";
import { LIVE_FRAME_EVENT, LIVE_RESYNC_EVENT } from "../live";
import { sharedEventBus } from "../osnovaContext";
import { getLocale } from "../i18n/i18n";
import { getProductDictRevision, invalidateProductDicts, loadProductDicts, resolveProductLabels, subscribeProductDict } from "../i18n/productDict";
// Подписка на словари продуктов: перерисовка, когда словарь пришёл или сменился язык.
export function useProductDictRevision() {
    return useSyncExternalStore(subscribeProductDict, getProductDictRevision);
}
// latestGuard — поколения запусков: результат применяем, только если запуск остался последним.
export function latestGuard() {
    let gen = 0;
    return () => {
        const mine = ++gen;
        return () => mine === gen;
    };
}
// changedStatusSlugs — слаги, чей статус или версия отличаются от запомненных прежде (новый
// продукт не считается — его словарь ещё не кеширован, invalidate для него бессмысленен). Снимок
// prevSnapshot обновляется на месте: вызывающий вызывает один раз на каждый reload.
export function changedStatusSlugs(prevSnapshot, list) {
    const changed = [];
    for (const p of list) {
        const prev = prevSnapshot.get(p.product);
        if (prev !== undefined && (prev.status !== p.status || prev.version !== p.version))
            changed.push(p.product);
    }
    for (const p of list)
        prevSnapshot.set(p.product, { status: p.status, version: p.version });
    return changed;
}
export function useProducts() {
    const [rawProducts, setProducts] = useState([]);
    const [productsReady, setProductsReady] = useState(false);
    const guard = useRef(latestGuard()).current;
    // Снимок статуса+версии каждого продукта на момент последнего reload — invalidate словаря
    // только когда статус или версия реально сменились (откат/обновление продукта), а не на каждый
    // ресинк живого канала (его onopen — сигнал о связи, не об обновлении продукта; словарь слага
    // иначе переспрашивался бы заново при каждом переподключении SSE на слабом стенде, см. PROB Х6;
    // версия добавлена, т.к. обновление продукта, случившееся при оборванном канале, иначе не
    // отличить от простого переподключения — статус остаётся прежним "ok").
    const prevStatus = useRef(new Map()).current;
    const reloadProducts = useCallback(() => {
        const isLatest = guard();
        platformMenu.getInstalledProducts()
            // рельс показываем после словарей всех продуктов — подписи берутся из них
            .then(async (list) => {
            const slugs = list.map((p) => p.product);
            const changed = changedStatusSlugs(prevStatus, list);
            if (changed.length > 0)
                invalidateProductDicts(changed);
            await loadProductDicts(slugs);
            if (!isLatest())
                return;
            setProducts(list);
            setProductsReady(true);
        })
            // сеть легла/сервер ответил ошибкой — честный empty-state, не падение
            .catch(() => { if (isLatest())
            setProductsReady(true); });
    }, [guard, prevStatus]);
    useEffect(() => { reloadProducts(); }, [reloadProducts]);
    // Смена языка: догружаем словари нового языка, до их прихода остаётся прежний текст.
    const revision = useProductDictRevision();
    const locale = getLocale();
    useEffect(() => {
        if (rawProducts.length > 0)
            void loadProductDicts(rawProducts.map((p) => p.product));
    }, [rawProducts, locale]);
    const products = useMemo(() => resolveProductLabels(rawProducts), [rawProducts, revision]);
    // Обрыв живого канала — best-effort доставка, БД остаётся источником истины: на open
    // потока перечитываем снимок счётчиков/списка продуктов снимком, не веря пропущенным кадрам.
    useEffect(() => sharedEventBus.on(LIVE_RESYNC_EVENT, reloadProducts), [reloadProducts]);
    // Живой кадр counter патчит бейдж двери без перезагрузки; total — уже сумма всех ключей
    // продукта (В2 «всё непрочитанное продукта»), сервер посчитал её при публикации.
    useEffect(() => sharedEventBus.on(`${LIVE_FRAME_EVENT}:counter`, (data) => {
        const frame = data;
        const payload = frame?.payload;
        if (!frame?.product || typeof payload?.total !== "number")
            return;
        const total = payload.total;
        setProducts((prev) => prev.map((p) => (p.product === frame.product && !p.role ? { ...p, count: total } : p)));
    }), []);
    // Живой кадр product.state патчит ДВЕРЬ точечно: ok/unhealthy правят статус на
    // месте, removed убирает все записи продукта (menu_items + профиль) без перезагрузки страницы.
    useEffect(() => sharedEventBus.on(`${LIVE_FRAME_EVENT}:product.state`, (data) => {
        const frame = data;
        const payload = frame?.payload;
        const product = payload?.product ?? frame?.product;
        const state = payload?.state;
        if (!product || !state)
            return;
        if (state === "removed") {
            setProducts((prev) => prev.filter((p) => p.product !== product));
            return;
        }
        const status = state === "ok" ? "ok" : "offline";
        if (state === "ok") {
            invalidateProductDicts([product]);
            void loadProductDicts([product]);
        }
        // снимок статуса правим здесь же — иначе следующий resync увидит "смену" и переспросит словарь повторно;
        // версию кадр product.state не несёт — сохраняем прежнюю, её сверит сам resync по ответу API.
        prevStatus.set(product, { status, version: prevStatus.get(product)?.version });
        setProducts((prev) => prev.map((p) => (p.product === product ? { ...p, status } : p)));
    }), []);
    return { products, productsReady };
}
